import React from 'react';
import {
    Header,
    Subheader,
    Caption,
    Paragraph,
    Flex,
    useStyles,
    useTheme,
    useAppDisplayModeContext,
    pxToRem,
    Button,
    Avatar,
} from '@archipro-design/aria';
import { Link } from '@remix-run/react';
import * as S from './ArticleDetails.style';
import type { ArticleDetailsProps } from '@modules/article/type';
import { getNameInitials } from '~/modules/user/util/get-name-initials';
import { superscriptize } from '~/utils';
import { useLocalisation } from '@archipro-website/localisation';
import { getFormattedLocalisedDate } from '@modules/date-time/util/get-localised-date';
import { useIsDisabledInPreview } from '@modules/root/hook/use-is-disabled-in-preview';
import { SaveToDesignBoardDiv } from './SaveToDesignBoardDiv';
import { useSaveToDesignBoard } from '~/modules/design-board/hook/use-save-to-design-board';

const ArticleDetails = ({
    id,
    title,
    publishDate,
    readingMinutes,
    author,
    professional,
}: Pick<
    ArticleDetailsProps,
    | 'id'
    | 'title'
    | 'publishDate'
    | 'readingMinutes'
    | 'author'
    | 'professional'
>) => {
    const isApStaff = author.archiProEditorial;
    const { css } = useStyles({ isApStaff });
    const theme = useTheme();
    const { localiseDate } = useLocalisation();
    const formattedPublishDate = getFormattedLocalisedDate(
        localiseDate,
        publishDate
    );
    const { favouriteItem } = useSaveToDesignBoard({
        itemType: 'ArticlePin',
        itemId: id,
    });
    const { desktop } = useAppDisplayModeContext();
    const showAuthor = author.name || professional.Title;

    const authorAvatar = isApStaff ? author.avatar : professional.logo;
    const imgAvatarProps = authorAvatar
        ? {
              src: authorAvatar,
              alt: `${professional.Title} company logo`,
          }
        : undefined;
    const authorInitials = author.initials || getNameInitials(author.name);
    const apStaffAvatarBg = author.avatar
        ? 'unset'
        : theme.siteVariables.colors.charcoal[250];
    const transformedTitle = superscriptize(title);

    return (
        <Flex column className={css(S.ArticleDetailsContainer)}>
            <Header
                as={'h1'}
                className={css(S.ArticleHeading)}
                variables={{
                    fontSize: pxToRem(desktop ? 60 : 28),
                    letterSpacing: theme.siteVariables.letterSpacingNormal,
                }}
                dangerouslySetInnerHTML={{ __html: transformedTitle }}
            ></Header>
            <Flex
                hAlign={'center'}
                vAlign={'center'}
                className={css(S.AvatarAndAuthor)}
            >
                {desktop && (
                    <Avatar
                        image={imgAvatarProps}
                        initials={authorInitials}
                        size={80}
                        imageFit={'cover'}
                        variables={{
                            avatarImageBackgroundColor: isApStaff
                                ? 'unset'
                                : professional.logoBackgroundColor ||
                                  theme.siteVariables.colors.white['FFF'],
                            borderWidth: '0',
                            avatarBackgroundColor: isApStaff
                                ? apStaffAvatarBg
                                : 'unset',
                        }}
                    />
                )}
                <div>
                    {showAuthor && (
                        <ByAuthor author={author} professional={professional} />
                    )}
                    <Flex className={css(S.ExtraInfo)}>
                        <Paragraph>{formattedPublishDate}</Paragraph>
                        <Paragraph>&nbsp;•&nbsp;</Paragraph>
                        <Paragraph>{`${readingMinutes} min read`}</Paragraph>
                    </Flex>
                    <Flex className={css(S.ShareAndSaveContainer)}>
                        <SaveToDesignBoardDiv
                            className={css(S.SaveToDesignBoardWrapper)}
                            favouriteItem={favouriteItem}
                            buttonSize={desktop ? 16 : 14}
                        />
                    </Flex>
                </div>
            </Flex>
        </Flex>
    );
};
export default ArticleDetails;

const ByAuthor = ({
    author,
    professional,
}: Pick<ArticleDetailsProps, 'author' | 'professional'>) => {
    const isApStaff = author.archiProEditorial;
    const { css } = useStyles({ isApStaff });

    const byProfessional = isApStaff ? 'ArchiPro' : professional.Title;
    const disableProfileLink = useIsDisabledInPreview('article_view_profile');
    const linkProps =
        disableProfileLink || isApStaff
            ? { to: '' }
            : { as: Link, to: professional.link };

    return (
        <Flex hAlign={'center'} className={css(S.ArticleAuthor)}>
            <Paragraph>Written by</Paragraph>
            <Button
                variant="text"
                variables={{ buttonPadding: 0 }}
                disabled={disableProfileLink || isApStaff}
                {...linkProps}
            >
                <Flex>
                    {author.name && <Paragraph>{author.name}</Paragraph>}
                    <Paragraph>{`${
                        author.name ? `, ` : ''
                    }${byProfessional}`}</Paragraph>
                </Flex>
            </Button>
        </Flex>
    );
};

export const ArticleIntro = ({ introText }: ArticleDetailsProps) => {
    const { css } = useStyles();
    const theme = useTheme();

    const { desktop } = useAppDisplayModeContext();

    return (
        <Flex column className={css(S.ArticleIntroContainer)}>
            {desktop ? (
                <Subheader
                    className={css(S.IntroTextDesktop)}
                    variant={'03'}
                    variables={{
                        lineHeight: theme.siteVariables.lineHeightSmall,
                    }}
                >
                    {introText}
                </Subheader>
            ) : (
                <Caption
                    className={css(S.IntroTextMobile)}
                    variant={'01'}
                    variables={{
                        lineHeight: theme.siteVariables.lineHeightSmall,
                    }}
                >
                    {introText}
                </Caption>
            )}
        </Flex>
    );
};
